import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  checkEmptyInputValue,
  getFormattedFloatNumber,
} from '@pages/AdditionalServices/tabs/DeliveryForm/helpers';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import { UnitData } from '@services/requests/searchLoaders/unitLoader/UnitLoaderQuery';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomCargoCalcType, SelectionCustomCargoPalletParameters } from '../types';
import { CargoSelectionItemProps } from './types';

function CargoSelectionPallet({
  BaseData,
  selectionCargoParamaters,
  mainLanguage,
  position,
  isActionsDisabled,
  isDeletable,
  onChangeSelectionCargoParameters,
  onDeleteSelectionCargoParameters,
}: CargoSelectionItemProps) {
  const pallet = BaseData.units.find(
    (u) => u.id === selectionCargoParamaters.palletParameters.type
  );

  const { t } = useTranslation('Common');

  const isHeightInputInvalid = checkEmptyInputValue(
    selectionCargoParamaters.palletParameters.normativeHeightOfStacking
  );
  const isWeightInputInvalid = checkEmptyInputValue(
    selectionCargoParamaters.palletParameters.weight
  );
  const isQuantityInputInvalid = checkEmptyInputValue(
    selectionCargoParamaters.palletParameters.quantity
  );

  const handleChangeCargoSelectionPalletParameters = <
    T extends keyof SelectionCustomCargoPalletParameters
  >(
    key: T,
    value: SelectionCustomCargoPalletParameters[T]
  ) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      palletParameters: {
        ...selectionCargoParamaters.palletParameters,
        [key]: value,
      },
    });
  };

  const handleChangeCustomCargoCalcType = (type: CustomCargoCalcType) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      parametersCargoCalcType: type,
    });
  };

  const handleChangeCustomCargoType = (type: 'base' | 'pallet') => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      customCargoType: type,
    });
  };

  const handleChangePalletType = (pallet: UnitData) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      palletParameters: {
        ...selectionCargoParamaters.palletParameters,
        type: pallet.id,
        normativeHeightOfStacking: pallet.normative_height_of_stacking.toString(),
      },
    });
  };

  const handleGenerateLocalizedName = (defaultName: string, localizations: LocalizedMessage[]) => {
    const localization = localizations.find((l) => l.lang_id === mainLanguage);
    if (!localization || localization.message.length === 0) {
      return defaultName;
    }

    return localization.message;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant={'subtitle2'} color={'#707070'}>
        {t('Common:CargoPlace')} #{position}
      </Typography>
      <Box className={'cargo-types'}>
        <Typography component={'span'} variant={'h5'} className={'cargo-types__title'}>
          {t('Common:CalculationType')}
        </Typography>
        <Button
          size={'small'}
          variant={'outlined'}
          className={'not-selected'}
          color={'inherit'}
          disabled={isActionsDisabled}
          onClick={() => handleChangeCustomCargoCalcType('volume')}
        >
          {t('Common:Consignment')}
        </Button>
        <Button size={'small'} variant={'outlined'} sx={{ width: 118 }}>
          {t('Common:CargoUnit')}
        </Button>
        <Typography component={'span'} variant={'h5'} className={'cargo-types__title'}>
          {t('Common:InputType')}
        </Typography>
        <Button disabled={isActionsDisabled} size={'small'} variant={'outlined'}>
          {t('Common:Pallet')}
        </Button>
        <Button
          size={'small'}
          variant={'outlined'}
          className={'not-selected'}
          color={'inherit'}
          disabled={isActionsDisabled}
          onClick={() => handleChangeCustomCargoType('base')}
          sx={{ width: 118, whiteSpace: 'no ' }}
        >
          {t('Common:Dimensions')}
        </Button>
        <Box gridColumn={'span 3'} flex={'0 0 100%'}>
          <TextField
            fullWidth
            variant={'standard'}
            value={pallet?.id}
            disabled={isActionsDisabled}
            error={!pallet}
            focused={!pallet}
            select
            id={'pallet-type'}
            sx={{
              '& .MuiInput-input': {
                color: '#383838!important',
                fontStyle: 'normal!important',
                fontWeight: '600!important',
                lineHeight: '22px!important',
                textAlign: 'right!important',
                fontSize: '12px!important',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                  position="start"
                >
                  <Typography component={'span'} variant={'h5'}>
                    {t('Common:PalletType')}
                  </Typography>
                </InputAdornment>
              ),
            }}
            onChange={(event) => {
              const newValue = BaseData.units.find((p) => p.id === event.target.value);
              handleChangePalletType(newValue as UnitData);
            }}
          >
            {BaseData.units
              .filter((u) => u.unit_group === '4')
              .map((option) => (
                <MenuItem value={option.id} key={option.id}>
                  {handleGenerateLocalizedName(
                    option.unit_symbol,
                    option.localized_unit_symbols_data
                  )}
                </MenuItem>
              ))}
          </TextField>
        </Box>
      </Box>
      <Box className={'cargo-inputs-block-grid'}>
        <TextField
          className={'cargo-inputs-block__half-mobile width'}
          variant={'standard'}
          value={pallet ? pallet.width : 0}
          disabled
          InputProps={{
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:WidthM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          className={'cargo-inputs-block__half-mobile length'}
          variant={'standard'}
          value={pallet ? pallet.length : 0}
          disabled
          InputProps={{
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:LengthM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isHeightInputInvalid}
          focused={isHeightInputInvalid}
          fullWidth
          variant={'standard'}
          disabled={isActionsDisabled}
          className={'height'}
          value={selectionCargoParamaters.palletParameters.normativeHeightOfStacking}
          onFocus={() =>
            handleChangeCargoSelectionPalletParameters(
              'normativeHeightOfStacking',
              getFormattedFloatNumber('')
            )
          }
          onChange={(event) =>
            handleChangeCargoSelectionPalletParameters(
              'normativeHeightOfStacking',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:HeightM')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isWeightInputInvalid}
          focused={isWeightInputInvalid}
          fullWidth
          disabled={isActionsDisabled}
          variant={'standard'}
          className={'weight'}
          value={selectionCargoParamaters.palletParameters.weight}
          onFocus={() => handleChangeCargoSelectionPalletParameters('weight', parseInt(''))}
          onChange={(event) => {
            handleChangeCargoSelectionPalletParameters('weight', parseInt(event.target.value));
          }}
          type={'number'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:PalletWeightKg')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isQuantityInputInvalid}
          focused={isQuantityInputInvalid}
          fullWidth
          disabled={isActionsDisabled}
          variant={'standard'}
          className={'seats'}
          value={selectionCargoParamaters.palletParameters.quantity}
          onFocus={() => handleChangeCargoSelectionPalletParameters('quantity', parseInt(''))}
          onChange={(event) =>
            handleChangeCargoSelectionPalletParameters('quantity', parseInt(event.target.value))
          }
          type={'number'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:PalletSeatsPcs')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isDeletable && (
        <IconButton
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-15px',
          }}
          disabled={isActionsDisabled}
          size={'small'}
          onClick={onDeleteSelectionCargoParameters}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );
}

export default CargoSelectionPallet;
